import { combineReducers, configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';

import { whatsNewApi } from '@/features/whats-new/api/whatsNewApi';

import { authReducer } from '@/shared/authentication';
import { api } from '@/shared/redux/api';
import { calendarApi } from '@/shared/redux/api/calendar-api';
import { organizationApi } from '@/shared/redux/api/organization-api';
import { rtkQueryErrorLogger } from '@/shared/redux/api/rtkQueryErrorLogger';

import agencyReducer from '@/slices/agency-slice';

import { unsubscribeReducer } from '../components/UnsubscribeNewsletter/reducer';

import accountPreferencesReducer from './accountPreferences/reducer';
import { adminPageReducer } from './adminPage/reducers';
import { adminRootReducer } from './adminPanel/reducers';
import { agencyLogoReducer } from './agencyLogo/reducer';
import { areasReducer } from './areas/reducer';
import { calendarReducer } from './calendar/reducer';
import { dairyRevenueProtectionRootReducer } from './dairyRevenueProtection/reducers';
import salesStatisticsReducer from './drpSalesStatistics/reducer';
import educationModuleReducer from './educationModule/reducer';
import { endorsementRootReducer } from './endorsement/reducers';
import { historicalDataReducer } from './historicalData/reducer';
import { insurancePlanReducer } from './insurancePlan/reducer';
import { loadersReducer } from './loaders/reducer';
import lrpReducer from './lrp/reducer';
import { managementReportsReducer } from './managementReports/reducers';
import { operationContactReducer } from './operationContact/reducer';
import { operationFiltersReducer } from './operationFilters/reducer';
import { operationPageReducer } from './operationPage/reducer';
import { operationPreferencesReducer } from './operationPreferences/reducer';
import { priceReportReducer } from './priceReport/reducer';
import { priceTrendsReducer } from './priceTrends/reducer';
import { projectionDataReducer } from './projectionData/reducer';
import rootSaga from './rootSaga';
import { scenarioReducer } from './scenario/reducer';
import { scenarioAnalysisReducer } from './scenarioAnalysis/reducers';
import tagReducer from './tags/reducer';
import tagTypeReducer from './tagTypes/reducer';
import { userAgentReducer } from './userAgent/reducer';

import type { StateType } from 'typesafe-actions';

const sagaMiddleware = createSagaMiddleware();
const listenerMiddleware = createListenerMiddleware();

export const rootReducer = combineReducers({
  areas: areasReducer,
  agency: agencyReducer,
  authentication: authReducer,
  dairyRevenueProtection: dairyRevenueProtectionRootReducer,
  adminPage: adminPageReducer,
  adminPanel: adminRootReducer,
  loaders: loadersReducer,
  scenario: scenarioReducer,
  salesStatistics: salesStatisticsReducer,
  educationModule: educationModuleReducer,
  endorsement: endorsementRootReducer,
  operationPage: operationPageReducer,
  historicalData: historicalDataReducer,
  operationContact: operationContactReducer,
  agencyLogo: agencyLogoReducer,
  operationFilters: operationFiltersReducer,
  accountPreferences: accountPreferencesReducer,
  operationPreferences: operationPreferencesReducer,
  priceReport: priceReportReducer,
  priceTrends: priceTrendsReducer,
  calendar: calendarReducer,
  projectedData: projectionDataReducer,
  lrp: lrpReducer,
  scenarioAnalysis: scenarioAnalysisReducer,
  managementReports: managementReportsReducer,
  insurancePlan: insurancePlanReducer,
  tags: tagReducer,
  tagTypes: tagTypeReducer,
  unsubscribe: unsubscribeReducer,
  userAgent: userAgentReducer,
  [api.reducerPath]: api.reducer,
  [organizationApi.reducerPath]: organizationApi.reducer,
  [calendarApi.reducerPath]: calendarApi.reducer,
  [whatsNewApi.reducerPath]: whatsNewApi.reducer
});

export type RootState = StateType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

const store = configureStore({
  reducer: rootReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false
    }).prepend(
      api.middleware,
      organizationApi.middleware,
      calendarApi.middleware,
      whatsNewApi.middleware,
      rtkQueryErrorLogger,
      sagaMiddleware,
      listenerMiddleware.middleware
    ),
  devTools: process.env.NODE_ENV !== 'production'
});

export { listenerMiddleware };
export default store;

sagaMiddleware.run(rootSaga);
